import React from 'react';
import c from 'classnames';
import { useSelector } from 'react-redux';
import { SearchkitWrapper } from '@uncinc/react-searchkit';
import { DEFAULT_REQUEST_OPTIONS } from '@uncinc/react-searchkit/dist/config/constants';
import type { SearchkitWrapperProps } from '@uncinc/react-searchkit/dist/components/Wrapper';

import {
  APP_OPTIONS,
  SEARCHKIT_HOST_URL,
  DOMAIN_ACCESS_REGENBOOG,
  DOMAIN_ACCESS_ANNUAL_REPORT_REGENBOOG,
} from '../../config/constants';
import {
  facetAttributes,
  resultAttributes,
  searchAttributes,
  sorting,
  highlightAttributes,
} from './config';

import './style.scss';

// Subcomponents.
import Facets from './Facets';
import Pagination from './Pagination';
import Results from './Results';
import SearchBar from './SearchBar';
import SortBy from './SortBy';
import Stats from './Stats';

interface SearchAllContentSubcomponents {
  Facets: typeof Facets;
  Pagination: typeof Pagination;
  Results: typeof Results;
  SearchBar: typeof SearchBar;
  SortBy: typeof SortBy;
  Stats: typeof Stats;
}

const SEARCH_ITEMS_PER_PAGE = 10;

export type SearchAllContentProps = Omit<SearchkitWrapperProps, 'connection' | 'indexName' | 'searchSettings'>;

// eslint-disable-next-line max-len
const SearchAllContent: React.FC<SearchAllContentProps> & SearchAllContentSubcomponents = (props) => {
  const {
    className,
    children,
    routing = true,
    ...restProps
  } = props;

  const classNames = c('SearchAllContent', className);
  const currentLanguage = useSelector<Immutable.Map<any, any>>((state) => state.getIn(['ui', 'language']));


  // Content must have the current language
  // Content should be in domein RGB and Jaarverslag OR it should be a document.
  const requestOptions: SearchkitWrapperProps['requestOptions'] = {
    ...DEFAULT_REQUEST_OPTIONS,
    getBaseFilters: () => [
      {
        bool: {
          must: {
            term: {
              combined_language_code: {
                value: currentLanguage,
              },
            },
          },
        },
      },
      {
        bool: {
          should: [
            {
              term: {
                combined_domain_access: {
                  value: DOMAIN_ACCESS_ANNUAL_REPORT_REGENBOOG,
                },
              },
            },
            {
              term: {
                combined_domain_access: {
                  value: DOMAIN_ACCESS_REGENBOOG,
                },
              },
            },
            {
              exists: {
                field: 'filename',
              },
            },
          ],
        },
      },
    ],
  };

  return (
    <SearchkitWrapper
      {...restProps}
      className={classNames}
      connection={{ host: SEARCHKIT_HOST_URL }}
      indexName={APP_OPTIONS.parameters.searchkitIndexAllContent}
      routing={routing}
      hitsPerPage={SEARCH_ITEMS_PER_PAGE}
      initialUiState={{
        [APP_OPTIONS.parameters.searchkitIndexAllContent]: {
          sortBy: `${APP_OPTIONS.parameters.searchkitIndexAllContent}_created`,
        },
      }}
      searchSettings={{
        search_attributes: searchAttributes,
        result_attributes: resultAttributes,
        facet_attributes: facetAttributes,
        highlight_attributes: highlightAttributes,
        sorting,
      }}
      requestOptions={requestOptions}
    >
      {children}
    </SearchkitWrapper>
  );
};

SearchAllContent.Facets = Facets;
SearchAllContent.Pagination = Pagination;
SearchAllContent.Results = Results;
SearchAllContent.SearchBar = SearchBar;
SearchAllContent.SortBy = SortBy;
SearchAllContent.Stats = Stats;

export default SearchAllContent;
